import { Box, Tooltip, Typography } from "@mui/material";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ComparisonBlock } from "@foodpilot/foods";
import { formatNumber } from "src/utils/formatting";
import { ScoreImprovement } from "src/types";
import { NumericalScoreValue, ScoreSourceMeta } from "src/api/client-api/olapScores/olapScoresSchema";

export type ScoreComparisonProps = {
    referenceScoreValue: number;
    scoreValueToCompare: NumericalScoreValue;
    unit?: string;
    improvement: ScoreImprovement;
    reportName: string;
    /**
     * When set, the displayed score is the platform reference value (no SSQ filled).
     * Adds an explanatory tooltip and tweaks the rendering so the user understands
     * that the value is inherited from the reference instead of being computed from
     * supplier answers. See plan ingredient-reference-scores-fallback (fe-7).
     */
    isInheritedFromReference?: boolean;
    /**
     * Per-supplier (or fallback) source metadata returned by the OLAP endpoint.
     * When present and meaningful (anything but `ssq`/`none`), the tooltip is
     * upgraded from the generic "inherited from reference" copy to a precise,
     * source-aware sentence (e.g. "Score provided by an ingredient reference
     * assigned at the supplier level — Valrhona"). Plan §FE-11.
     */
    sourceMeta?: ScoreSourceMeta | null;
};

export const ScoreComparison = (props: ScoreComparisonProps) => {
    const { t, i18n } = useTranslation();
    const {
        scoreValueToCompare,
        referenceScoreValue,
        unit,
        reportName,
        isInheritedFromReference = false,
        sourceMeta,
    } = props;

    // Source-aware tooltip text takes precedence; we keep the legacy literal
    // as the fallback so unrelated callers (no `sourceMeta` wired) keep their
    // current behaviour verbatim.
    const inheritedTooltipText =
        getSourceAwareTooltip(t, sourceMeta) ?? t("score_reference.inherited_from_reference_tooltip");

    // Defensive fallback (fe-6): if the API failed to return a numeric value but we still
    // know a reference value, surface it with a tooltip rather than collapsing to nothing.
    if (scoreValueToCompare === null) {
        if (referenceScoreValue === null || referenceScoreValue === undefined) {
            return null;
        }

        const formattedReference = formatNumber(referenceScoreValue, i18n.resolvedLanguage, {
            maximumFractionDigits: 2,
        });

        return (
            <Tooltip title={inheritedTooltipText} arrow>
                <Box
                    sx={{
                        display: "inline-flex",
                        gap: "0.4rem",
                        alignItems: "baseline",
                        cursor: "help",
                        textDecoration: "underline dotted",
                        textUnderlineOffset: "3px",
                    }}
                >
                    <Typography variant="body" sx={{ fontWeight: 700 }}>
                        {formattedReference}
                        {unit ? ` ${unit}` : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {reportName}
                    </Typography>
                </Box>
            </Tooltip>
        );
    }

    const block = (
        <Box
            sx={{
                display: "flex",
                gap: "0.6rem",
                alignItems: "center",
                ...(isInheritedFromReference && {
                    textDecoration: "underline dotted",
                    textUnderlineOffset: "3px",
                    cursor: "help",
                }),
            }}
        >
            <ComparisonBlock
                currentValue={scoreValueToCompare}
                layout={{
                    direction: "horizontal",
                    size: "M",
                }}
                comparedTo={{
                    value: referenceScoreValue,
                    unit: unit,
                    label: reportName,
                }}
                improvement={props.improvement}
                comparisonText={t("score_reference.in_relation")}
            />
        </Box>
    );

    if (isInheritedFromReference) {
        return (
            <Tooltip title={inheritedTooltipText} arrow>
                {block}
            </Tooltip>
        );
    }

    return block;
};

/**
 * Map a `ScoreSourceMeta` payload to a human-readable tooltip string. Returns
 * `null` for "default" sources (`ssq`, `none`, missing meta) so the caller can
 * fall back to its existing copy. Mirrors the wording used by `ScoreSourceChip`
 * to keep the chip ↔ tooltip story coherent.
 *
 * The copy is intentionally neutral — these are normal cascade outcomes, not
 * "legacy" paths (cf. product feedback Apr-26).
 */
const getSourceAwareTooltip = (t: TFunction, meta?: ScoreSourceMeta | null): string | null => {
    if (!meta) return null;
    switch (meta.source) {
        case "supplier_reference":
            return meta.owner?.name ?
                    t("score_source.supplier_reference.tooltip_with_owner", {
                        defaultValue: "Score from {{name}}'s supplier reference.",
                        name: meta.owner.name,
                    })
                :   t("score_source.supplier_reference.tooltip", {
                        defaultValue: "Score from a supplier reference.",
                    });
        case "ingredient_reference":
            return t("score_source.ingredient_reference.tooltip", {
                defaultValue: "Score from a default ingredient (platform reference).",
            });
        case "sure":
            return t("score_source.sure.tooltip", {
                defaultValue: "Score from the Sure reference.",
            });
        case "ssq":
        case "none":
            return null;
    }
};
